import * as React from 'react'

import { Map as MapboxMap } from 'mapbox-gl'
import { action, makeObservable, observable } from 'mobx'

import { projectToOverlay } from '~/client/src/shared/components/MapBoxViewer/projectToOverlay'
import { createGripDrag } from '~/client/src/shared/utils/createGripDrag'

interface ICoordinates {
  latitude: number
  longitude: number
}

export interface IMapAnchoredCardOptions {
  getMap: () => MapboxMap
  onMapChange: (callback: (map: MapboxMap | null) => void) => () => void
  // The map coordinate the card is glued to; null hides the card.
  getCoordinates: () => ICoordinates
  anchor: {
    // Horizontal clearance between the anchor's right-most extent and the card.
    gapPx: number
    // Distance from the anchor's projected point to its right-most visual extent.
    rightExtentPx: number
    // How far above the projected point the anchor's visual center sits.
    risePx: number
  }
  // Vertical alignment: measure the offset (within the card wrapper) that should
  // sit level with the anchor's visual center. Return null when unmeasurable —
  // the controller reuses the last non-null value, so the card doesn't jump
  // while its DOM swaps (e.g. a full card being replaced by a minimized pill).
  measureAlignOffset?: (wrapper: HTMLElement) => number
  // Px kept clear below the card when clamping to the overlay bottom, so a control
  // docked beneath it (the markup toolbar) stays on-screen. 0/undefined = no reserve.
  getBottomReservePx?: () => number
  // Called at the end of every reproject with the card's final overlay-space box, so a
  // consumer can position a docked control in the SAME frame without re-reading the DOM
  // (avoids a second layout reflow per camera frame — see the desktop toolbar docking).
  onReproject?: (box: {
    x: number
    y: number
    width: number
    height: number
  }) => void
}

// Glues a positioned card to a map coordinate: projects the anchor into overlay
// space, re-anchors on every camera move/frame, lets the user drag the card
// aside (the drag offset stays pin-relative), and clamps the card on-screen.
export default class MapAnchoredCardController {
  // One-shot visibility flag: per-frame position goes straight to the wrapper DOM —
  // an observable position would reconcile the heavy card tree every camera frame.
  @observable public hasPosition = false
  private lastX: number = null
  private lastY: number = null
  // Manual drag delta applied on top of the anchor, so the user can pull the
  // card aside while it keeps following the anchor as the map moves.
  @observable.ref private dragOffset: { x: number; y: number } = { x: 0, y: 0 }
  // Last pointer pos during a drag + the "is dragging" flag reproject reads.
  private dragStart: { px: number; py: number } = null
  // The positioned wrapper the card lives in; measured to keep the projected
  // card on-screen.
  public readonly wrapperRef = React.createRef<HTMLDivElement>()
  private boundMap: MapboxMap = null
  private alignOffset = 0
  private isFrozen = false
  private readonly reanchor = (): void => this.reproject()

  public constructor(private readonly options: IMapAnchoredCardOptions) {
    makeObservable(this)
  }

  private disposeMapWatch: () => void = null

  // 'render' alone covers every camera frame (a camera change always paints) plus
  // tile fades; adding 'move' would double reproject's DOM reads per frame.
  public startAnchoring(): void {
    this.stopAnchoring()
    // Waits for the notification rather than polling a frame at a time: rAF is frozen while
    // the tab is hidden, which is when a context loss lands.
    this.disposeMapWatch = this.options.onMapChange(
      map => map && this.startAnchoring(),
    )
    const map = this.options.getMap()
    if (!map) {
      return
    }
    this.boundMap = map
    map.on('render', this.reanchor)
    this.reproject()
  }

  public stopAnchoring(): void {
    this.disposeMapWatch?.()
    this.disposeMapWatch = null
    this.boundMap?.off('render', this.reanchor)
    this.boundMap = null
    this.cardDrag.stop()
    // Fresh binding must re-reveal + re-write from scratch (a remounted map/wrapper
    // would otherwise stay frozen at the stale position).
    this.resetPosition()
  }

  @action
  private resetPosition(): void {
    this.hasPosition = false
    this.lastX = null
    this.lastY = null
  }

  // Incremental delta (not absolute-from-start): reproject re-bases the offset onto the
  // clamped position each move, so it can't over-accumulate at an edge and feel "stuck".
  private readonly cardDrag = createGripDrag({
    onStart: e => {
      this.dragStart = { px: e.clientX, py: e.clientY }
    },
    onMove: e => {
      if (!this.dragStart) {
        return
      }
      const dx = e.clientX - this.dragStart.px
      const dy = e.clientY - this.dragStart.py
      this.dragStart.px = e.clientX
      this.dragStart.py = e.clientY
      this.setDragOffset(this.dragOffset.x + dx, this.dragOffset.y + dy)
      this.reproject()
    },
    onEnd: () => {
      this.dragStart = null
    },
  })

  public readonly startCardDrag = (e: React.PointerEvent): void =>
    this.cardDrag.onPointerDown(e)

  @action
  private setDragOffset(x: number, y: number): void {
    this.dragOffset = { x, y }
  }

  /** Holds the card still while the capture moves the camera out from under it. */
  public freeze(): void {
    this.isFrozen = true
  }

  public unfreeze(): void {
    if (!this.isFrozen) {
      return
    }
    this.isFrozen = false
    this.reproject()
  }

  // Project the anchor coordinate to overlay space, place the card to its
  // right — left edge clear of the anchor's right-most extent, align target
  // vertically level with the anchor's visual center — then clamp on-screen.
  @action.bound
  public reproject(): void {
    if (this.isFrozen) {
      return
    }
    const coords = this.options.getCoordinates()
    const map = this.boundMap
    const wrapper = this.wrapperRef.current
    const overlay = wrapper?.offsetParent as HTMLElement
    if (!coords || !map || !wrapper || !overlay) {
      return
    }
    const { gapPx, rightExtentPx, risePx } = this.options.anchor
    const pin = projectToOverlay(
      map,
      coords.longitude,
      coords.latitude,
      overlay,
    )
    const measured = this.options.measureAlignOffset?.(wrapper)
    if (measured != null) {
      this.alignOffset = measured
    }
    const cardW = wrapper.offsetWidth
    const cardH = wrapper.offsetHeight
    const maxX = Math.max(overlay.clientWidth - cardW, 0)
    // Keep a control docked below the card (the markup toolbar) on-screen: floor the
    // card's bottom above the reserved strip instead of letting it touch the overlay edge.
    const bottomReserve = this.options.getBottomReservePx?.() ?? 0
    const maxY = Math.max(overlay.clientHeight - cardH - bottomReserve, 0)
    const finalX = Math.min(
      Math.max(pin.x + rightExtentPx + gapPx + this.dragOffset.x, 0),
      maxX,
    )
    const finalY = Math.min(
      Math.max(pin.y - risePx - this.alignOffset + this.dragOffset.y, 0),
      maxY,
    )
    // DOM write, not React — the card tree must not reconcile per camera frame.
    if (finalX !== this.lastX || finalY !== this.lastY) {
      this.lastX = finalX
      this.lastY = finalY
      wrapper.style.transform = `translate3d(${finalX}px, ${finalY}px, 0)`
    }
    if (!this.hasPosition) {
      this.setHasPosition()
    }
    // While dragging, collapse the offset onto the clamped position so a reversal
    // moves the card immediately — no unbounded over-pull accumulates at an edge.
    if (this.dragStart) {
      this.setDragOffset(
        finalX - (pin.x + rightExtentPx + gapPx),
        finalY - (pin.y - risePx - this.alignOffset),
      )
    }
    this.options.onReproject?.({
      x: finalX,
      y: finalY,
      width: cardW,
      height: cardH,
    })
  }

  @action
  private setHasPosition(): void {
    this.hasPosition = true
  }
}
